<template>
  <v-form ref="serverOptionForm">
    <v-card elevation="0" flat tile>
      <v-card-text>
        <v-row justify="space-around">
          <v-col cols="12" md="12">
            <!-- Scrollview for server configuration -->
            <v-row justify="space-around">
              <v-col cols="12" md="6">
                <!-- Channel -->
                <v-select
                    :items="options"
                    item-text="name"
                    item-value="id"
                    :menu-props="{bottom: true, offsetY: true}"
                    v-model="optionKey"
                    outlined
                    clearable
                    @change="selectType"
                    :label="$t('components:server-option-form.option-key')"
                    hide-details="auto"
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                />
              </v-col>
              <!-- Option type -->
              <!-- depending on the selected type, we offer different fields with different rules -->
              <v-col cols="12" md="6">
                <!-- NO SELECTION -->
                <v-text-field
                  v-if="optionType == null"
                  outlined
                  disabled
                  hide-details
                  :label="$t('components:server-option-form.no-option')"
                />
                <!-- BOOLEAN -->
                <v-select
                    v-else-if="optionType == 'BOOLEAN'"
                    :items="booleanValues"
                    v-model="optionValue"
                    outlined
                    :menu-props="{ bottom: true, offsetY: true }"
                    :label="$t('components:server-option-form.option-value')"
                    tabindex="7"
                    hide-details="auto"
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                />
                <!-- INTEGER -->
                <v-text-field
                    v-else-if="optionType == 'INTEGER'"
                    outlined
                    v-model="optionValue"
                    :label="$t('components:server-option-form.option-value')"
                    hide-details="auto"
                    type="number"
                    :rules="[ruleIsInteger($t('utils:rules.onlynumerictext'))]"
                />
                <!-- DECIMAL -->
                <v-text-field
                    v-else-if="optionType == 'DECIMAL'"
                    outlined
                    v-model="optionValue"
                    :label="$t('components:server-option-form.option-value')"
                    hide-details="auto"
                    type="number"
                    :rules="[ruleIsNumber($t('utils:rules.only-number'))]"
                />
                <!-- COUNTER -->
                <v-text-field
                    v-else-if="optionType == 'COUNTER'"
                    outlined
                    v-model="optionValue"
                    :label="$t('components:server-option-form.option-value')"
                    hide-details="auto"
                    :rules="[ruleIsOptionCounter($t('utils:rules.option-counter'))]"
                />
                <!-- TEXT -->
                <v-text-field
                    v-else
                    outlined
                    v-model="optionValue"
                    :label="$t('components:server-option-form.option-value')"
                    hide-details="auto"
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                />
              </v-col>
            </v-row>

            <v-btn
                class="mb-5"
                elevation="2"
                color="primary"
                @click="addOption"
            >{{ $t('buttons:add') }}
            </v-btn>

            <v-card outlined class="mb-5">
              <v-virtual-scroll
                :items="selectedOptions"
                :item-height="50"
                height="200"
              >
                <template v-slot="{ item }">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title selectable @click="selectItem(item)">{{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon @click="deleteOptions(item)" v-on="on">close</v-icon>
                        </template>
                        <span>{{ $t('buttons:delete') }}</span>
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider></v-divider>
                </template>
              </v-virtual-scroll>
            </v-card>

            <!-- Option Name -->
            <v-text-field
              outlined
              disabled
              v-model="optionName"
              :label="$t('components:server-option-form.option-name')"
            />

            <!-- Option description -->
            <v-textarea
              outlined
              disabled
              v-model="optionDescription"
              :label="$t('components:server-option-form.option-description')"
              auto-grow
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import OptionsService from '@/modules/display/services/option-service';
import Utils from "@/modules/core/services/utils";
import {ruleRequired, ruleIsInteger, ruleIsNumber, ruleIsOptionCounter} from "@/modules/core/services/rules";

export default {
  name: "ServerOptionForm",
  props: {
    startOptions: Array, //passes the first data from the loaded object to our form
  },
  data() {
    return {
      selectedOption: null,
      optionName: null,
      optionDescription: null,
      selectedOptions: [],
      options: [],
      optionKey: null,
      optionValue: null,
      optionType: null
    }
  },
  computed: {
    booleanValues() {
      return [
        {value: "false", text: this.$t('buttons:no')},
        {value: "true", text: this.$t('buttons:yes')},
      ]
    },
  },
  methods: {
    loadOptions() {
      OptionsService.readOptions(
        (response) => {
          this.options = response.data;
        },
        (error) => {
          this.$logger.error("Error at elevator saving: " + error);
          Utils.showFailureSnackbar(this.$t('load-failure', {name: this.$t('components:server-option-form.options')}));
        }
      )
    },

    initialSelectedOptions() {
      if (typeof this.startOptions === 'undefined' || this.startOptions === null) {
        return;
      }
      this.initialiseOptionsWithValues(this.startOptions);
    },

    initialiseOptionsWithValues(options){
      for (let i = 0; i < options.length; i++) {
        this.selectedOptions.push({
          id: options[i].id,
          name: options[i].name + ': ' + options[i].value,
          value: options[i].value,
          optionId: options[i].optionId
        });
      }
    },

    selectItem(selectedItem) {
      this.selectType(selectedItem.optionId);
      this.optionKey = selectedItem.optionId;
      this.optionValue = selectedItem.value;
    },

    deleteOptions(option) {
      for (let i = 0; i < this.selectedOptions.length; i++) {
        if (this.selectedOptions[i].optionId == option.optionId) {
          this.selectedOptions.splice(i, 1);
          this.$emit('updateOptions',this.selectedOptions);
          return;
        }
      }
    },

    selectType(typeId) {
      this.optionValue = null;
      if (typeof typeId === 'undefined' || typeId === null) {
        this.optionName = '';
        this.optionDescription = '';
        this.optionType = null;
        this.selectedOption = null;
        return;
      }

      for (let i = 0; i < this.options.length; i++) {
        if (this.options[i].id == typeId) {
          this.selectedOption = this.options[i];
          this.optionName = this.selectedOption.name;
          this.optionDescription = this.selectedOption.description;
          this.optionType = this.selectedOption.type;
          break;
        }
      }
    },

    addOption() {
      if (typeof this.selectedOption === 'undefined'
        || this.selectedOption == null
        || typeof this.optionValue === 'undefined'
        || this.optionValue == null) {
        return;
      }

      if (!this.$refs.serverOptionForm.validate()) {
        return;
      }

      for (let i = 0; i < this.selectedOptions.length; i++) {
        if (this.selectedOptions[i].optionId == this.selectedOption.id) {
          this.selectedOptions[i].value = this.optionValue;
          this.selectedOptions[i].name = this.selectedOption.name + ': ' + this.optionValue;
          this.$emit('updateOptions',this.selectedOptions);
          return;
        }
      }

      this.selectedOptions.push({
        id: '',
        name: this.selectedOption.name + ': ' + this.optionValue,
        value: this.optionValue,
        optionId: this.selectedOption.id
      });
      this.$emit('updateOptions',this.selectedOptions);

    },

    ruleRequired,
    ruleIsInteger,
    ruleIsNumber,
    ruleIsOptionCounter
  },
  created() {
    this.initialSelectedOptions();
    this.loadOptions();
  }
}
</script>
